import { useMatches } from "react-router";
import { HamburgerButton } from "./HamburgerButton";
import { NotificationDot } from "./NotificationDot";

/**
 * The breadcrumb trail comes from `handle.title` on every matched route in
 * router.tsx — the routes already know their own names, so there is no second
 * path→label map to keep in sync. A section shell contributes its title and its
 * child contributes the leaf, giving "Settings › Agreements" with no shell
 * rendering a breadcrumb bar of its own.
 */
function usePageTrail(): string[] {
  const matches = useMatches();
  const trail: string[] = [];
  for (const match of matches) {
    const title = (match.handle as { title?: string } | undefined)?.title;
    if (title && trail[trail.length - 1] !== title) trail.push(title);
  }
  return trail.length > 0 ? trail : ["Portal"];
}

function formatDate(date: Date): string {
  return date.toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  });
}

export function TopBar() {
  const trail = usePageTrail();
  const leaf = trail[trail.length - 1];
  const today = formatDate(new Date());

  return (
    <header className="sticky top-[var(--banner-h)] z-30 flex h-14 items-center gap-3 bg-paper/95 dark:bg-paper/70 px-4 backdrop-blur-sm dark:backdrop-blur-md hair-b md:px-6">
      {/* First focusable element on the page — skips the whole nav rail. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-3 focus:z-50 focus:rounded-md focus:bg-tide focus:px-3 focus:py-2 focus:text-[13px] focus:text-paper-hi"
      >
        Skip to content
      </a>

      <HamburgerButton />

      {/* Breadcrumb only. The page's h1 is rendered by PageHeader, in the
          content area — visibly on pages that show a title, screen-reader-only
          on the ones the breadcrumb already names. */}
      <nav
        aria-label="Breadcrumb"
        className="flex min-w-0 flex-1 items-center gap-1.5 text-[14px]"
      >
        {trail.slice(0, -1).map((crumb) => (
          <span key={crumb} className="hidden items-center gap-1.5 sm:flex">
            <span className="truncate text-ink-4">{crumb}</span>
            <span aria-hidden="true" className="text-ink-5">
              ›
            </span>
          </span>
        ))}
        <span className="truncate font-semibold">{leaf}</span>
      </nav>

      {/* Date — hidden on mobile */}
      <div className="hidden text-[12.5px] text-ink-4 md:block num">
        {today}
      </div>

      {/* Notification bell */}
      <NotificationDot />
    </header>
  );
}
